// =============================================================================
// TOAST CONTEXT & PROVIDER
// =============================================================================

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { Toast, ToastContextType, ToastOptions, ToastMethods } from '@/types/toast';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  defaultPosition?: 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';
  defaultDuration?: number;
}

export function ToastProvider({ 
  children, 
  defaultPosition = 'top-right',
  defaultDuration = 5000 
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addToast = useCallback((message: string, options: ToastOptions = {}): string => {
    const id = generateId();
    const toast: Toast = {
      id,
      type: options.type || 'info',
      title: options.title,
      message,
      duration: options.duration ?? defaultDuration,
      position: options.position || defaultPosition,
      action: options.action,
      dismissible: options.dismissible ?? true,
      createdAt: Date.now(),
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration (if not persistent)
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  }, [defaultPosition, defaultDuration, generateId]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const updateToast = useCallback((id: string, updates: Partial<ToastOptions>) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id 
        ? { ...toast, ...updates }
        : toast
    ));
  }, []);

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    updateToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType & ToastMethods {
  const context = useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  // Add convenience methods
  const success = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return context.addToast(message, { ...options, type: 'success' });
  }, [context]);

  const error = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return context.addToast(message, { ...options, type: 'error' });
  }, [context]);

  const warning = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return context.addToast(message, { ...options, type: 'warning' });
  }, [context]);

  const info = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return context.addToast(message, { ...options, type: 'info' });
  }, [context]);

  const loading = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return context.addToast(message, { ...options, type: 'loading', duration: 0 });
  }, [context]);

  return {
    ...context,
    success,
    error,
    warning,
    info,
    loading,
  };
}
